import {
  Button,
  Divider,
  Grid,
  Heading,
  Icon,
  Text,
  Modal,
  useIsDesignLanguage,
  DesignLanguage,
  AllIconNames,
  useDesignLanguagePerClass,
} from '@afterpaytouch/core'
import clsx from 'clsx'
import { useTranslation } from '../../utils/convergence'
import React, { FunctionComponent, useState } from 'react'
import { TrackOnMount, useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { useCheckoutSizes } from '../../utils/responsive'
import { useOAuthStateSelector } from '../../state'
import { Trans } from '../../utils/convergence'

import styles from './style.module.css'

interface Props {
  show: boolean
  onAccept: () => void
  onDecline: () => void
}

const RoundIcon: FunctionComponent<{ name: AllIconNames }> = ({ name }) => (
  <div className={styles.roundIcon}>
    <Icon name={name} color='White' />
  </div>
)

export const OAuthLoginConsentModal: FunctionComponent<Props> = ({ show, onAccept, onDecline }) => {
  const { t } = useTranslation()
  const { sectionHeadingSize, headingSize, smallTextSize, extraSmallTextSize } = useCheckoutSizes()
  const [readyAnim, setReadyAnim] = useState(false)

  const { clientName: merchantName } = useOAuthStateSelector()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const isBronteDesignLanguage = useIsDesignLanguage(DesignLanguage.Bronte)
  const designLanguageStyles = useDesignLanguagePerClass(styles)

  const onDeclineHandler = (): void => {
    logEvent('Checkout OAuth Account Link denied')
    onDecline()
  }

  const onAcceptHandler = (): void => {
    logEvent('Checkout OAuth Account Link Authorised')
    onAccept()
  }

  return (
    <Modal
      showCloseButton={isBronteDesignLanguage}
      show={show}
      onClose={onDeclineHandler}
      onBackdropClick={onDeclineHandler}
      onEntered={() => setReadyAnim(true)}
    >
      <Modal.Header>
        <div className={designLanguageStyles.header}>
          <Heading size={headingSize}>{t('password:promoteOAuthLogin.pageHeading')}</Heading>
          <Text className={designLanguageStyles.tagline} size={sectionHeadingSize} color={isBronteDesignLanguage ? 'Black' : 'Gray40'}>
            {t('password:promoteOAuthLogin.tagline', { context: merchantName ? 'merchant' : '', merchantName })}
          </Text>
        </div>
      </Modal.Header>
      <Modal.Content>
        <div className={clsx(readyAnim && styles.readyAnim)}>
          <div>
            <Grid>
              <Grid.FixedItem width={isBronteDesignLanguage ? 32 : 64}>
                {isBronteDesignLanguage ? <Icon name='Shop' /> : <RoundIcon name='Shop' />}
              </Grid.FixedItem>
              <div>
                <Heading size={sectionHeadingSize}>{t('password:promoteOAuthLogin.heading1')}</Heading>
                <Text size={smallTextSize} color='Gray40'>
                  {t('password:promoteOAuthLogin.text1', { context: 'oauth' })}
                </Text>
              </div>
            </Grid>
          </div>
          {isBronteDesignLanguage && (
            <div className={clsx(styles.animationWrapper)}>
              <div className={clsx(styles.uniqueSellingPoint, styles.usp1)}>
                <Divider kind='Hairline' />
                <Grid>
                  <Grid.FixedItem width={32}>
                    <Icon name='Profile' />
                  </Grid.FixedItem>
                  <div>
                    <Heading size={sectionHeadingSize}>{t('password:promoteOAuthLogin.heading2')}</Heading>
                    <Text size={smallTextSize} color='Gray40'>
                      {t('password:promoteOAuthLogin.text2')}
                    </Text>
                  </div>
                </Grid>
              </div>
            </div>
          )}
          <div className={clsx(styles.animationWrapper)}>
            <div className={clsx(styles.uniqueSellingPoint, styles.usp2)}>
              {isBronteDesignLanguage ? <Divider kind='Hairline' /> : <div className={styles.space} />}
              <Grid>
                <Grid.FixedItem width={isBronteDesignLanguage ? 32 : 64}>
                  {isBronteDesignLanguage ? <Icon name='Settings' /> : <RoundIcon name='Settings' />}
                </Grid.FixedItem>
                <div>
                  <Heading size={sectionHeadingSize}>{t('password:promoteOAuthLogin.heading3')}</Heading>
                  <Text size={smallTextSize} color='Gray40'>
                    {t('password:promoteOAuthLogin.text3')}
                  </Text>
                </div>
              </Grid>
            </div>
          </div>
          <div className={clsx(designLanguageStyles.animationWrapper, designLanguageStyles.footer)}>
            <div className={styles.content}>
              <Text size={isBronteDesignLanguage ? extraSmallTextSize : smallTextSize} color='Gray40'>
                <Trans i18nKey='password:promoteOAuthLogin.consent' values={{ merchantName }} />
              </Text>
            </div>
            <div className={clsx(styles.callToAction)}>
              <Button kind='Primary' padding='Fluid' size='Large' onClick={onAcceptHandler} aria-label='' testNameSpace='persistent-login-consent-accept'>
                {t('password:promoteOAuthLogin.ok')}
              </Button>
              <Button
                kind={isBronteDesignLanguage ? 'Ghost' : 'Secondary'}
                padding='Fluid'
                size='Large'
                onClick={onDeclineHandler}
                aria-label=''
                testNameSpace='persistent-login-consent-decline'
              >
                {t('password:promoteOAuthLogin.notNow')}
              </Button>
            </div>
          </div>
          <TrackOnMount eventName={'Viewed Account Link Request Page'} eventProperties={{ pageName: 'OAuth Consent Form' }} />
        </div>
      </Modal.Content>
    </Modal>
  )
}
